import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Image as ImageIcon, Film, Maximize2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";

export interface MediaEntry {
  nome: string;
  path: string;
  tipo: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  midias: MediaEntry[];
  personagemNome: string;
}

const BUCKET = "anuncios-personagens-midia";

const isVideoMedia = (media?: MediaEntry) => {
  if (!media) return false;
  const tipo = (media.tipo || "").toLowerCase();
  const path = (media.path || "").toLowerCase();
  return tipo.includes("video") || /\.(mp4|webm|mov|m4v|ogg)$/i.test(path);
};

const videoMimeFromPath = (path: string) => {
  const ext = path.split(".").pop()?.toLowerCase();
  if (ext === "webm") return "video/webm";
  if (ext === "ogg") return "video/ogg";
  if (ext === "mov") return "video/quicktime";
  return "video/mp4";
};

const MediaCarouselDialog = ({ open, onOpenChange, midias, personagemNome }: Props) => {
  const isMobile = useIsMobile();
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [videoBlobUrls, setVideoBlobUrls] = useState<Record<string, string>>({});
  const [videoLoading, setVideoLoading] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!open || midias.length === 0) return;
    setIndex(0);
    (async () => {
      const paths = midias.map((m) => m.path);
      const { data } = await supabase.storage.from(BUCKET).createSignedUrls(paths, 3600);
      const map: Record<string, string> = {};
      (data || []).forEach((d) => {
        const signedUrl = d.signedUrl || (d as { signedURL?: string }).signedURL;
        if (d.path && signedUrl) map[d.path] = signedUrl;
      });
      setUrls(map);
    })();
  }, [open, midias]);

  useEffect(() => {
    if (!open) {
      setVideoBlobUrls((existing) => {
        Object.values(existing).forEach((url) => URL.revokeObjectURL(url));
        return {};
      });
    }
  }, [open]);

  const current = midias[index];
  const currentUrl = current ? urls[current.path] : undefined;
  const currentIsVideo = isVideoMedia(current);
  const currentDisplayUrl = currentIsVideo && current ? videoBlobUrls[current.path] || currentUrl : currentUrl;
  const currentVideoMime = current ? videoMimeFromPath(current.path) : "video/mp4";
  const hasMany = midias.length > 1;

  useEffect(() => {
    if (!open || !current || !currentUrl || !currentIsVideo || videoBlobUrls[current.path]) return;
    let cancelled = false;
    setVideoLoading(true);
    fetch(currentUrl)
      .then((response) => {
        if (!response.ok) throw new Error("Failed to fetch video");
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(blob);
        setVideoBlobUrls((existing) => ({ ...existing, [current.path]: blobUrl }));
      })
      .catch(() => {
        // Keep the signed URL fallback in the player if blob loading is not available.
      })
      .finally(() => {
        if (!cancelled) setVideoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [current, currentIsVideo, currentUrl, open, videoBlobUrls]);

  const thumbnailItems = useMemo(() => midias, [midias]);
  const go = (dir: number) => {
    setIndex((i) => (i + dir + midias.length) % midias.length);
  };

  const openInNewWindow = async (url?: string) => {
    if (!url) return;
    // Open a single new tab and keep a handle to it. Note: using "noopener"
    // here makes window.open return null, which previously caused a SECOND
    // window to be opened as a fallback (leaving a blank tab behind).
    const opened = window.open("", "_blank");
    if (opened) {
      try {
        opened.opener = null;
      } catch {
        /* ignore */
      }
      opened.document.write('<!doctype html><title>Carregando mídia...</title><body style="margin:0;background:#050505;color:#d9c27a;display:grid;place-items:center;height:100vh;font-family:sans-serif">Carregando...</body>');
    }
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch media");
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      if (opened) {
        opened.location.href = blobUrl;
      } else {
        window.open(blobUrl, "_blank", "noopener,noreferrer");
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      if (opened) opened.location.href = url;
      else window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  if (midias.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${isMobile ? "max-w-[95vw]" : "max-w-5xl"} max-h-[95vh] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="font-pixel text-[11px] text-primary gold-text-glow truncate">
            {personagemNome} — Fotos/Vídeos ({midias.length})
          </DialogTitle>
          <DialogDescription className="sr-only">
            Galeria de fotos e vídeos do personagem, com navegação por setas e miniaturas.
          </DialogDescription>
        </DialogHeader>

        {/* Current item title */}
        <p className="font-pixel text-[9px] text-foreground text-center truncate">
          {current?.nome || "—"}
        </p>

        {/* Main viewer with side arrows */}
        <div className="relative flex items-center justify-center bg-stone-dark/40 tibia-border-inner rounded-sm overflow-hidden">
          {hasMany && (
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Anterior"
              className="absolute left-1.5 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-sm bg-stone-dark/80 tibia-border-inner text-primary hover:bg-stone-dark transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          <div className="relative w-full h-[62vh] sm:h-[68vh] flex items-center justify-center p-1">
            {currentDisplayUrl ? (
              currentIsVideo ? (
                <video
                  key={`${current?.path}-${currentDisplayUrl}`}
                  src={currentDisplayUrl}
                  controls
                  playsInline
                  disablePictureInPicture={false}
                  preload="metadata"
                  className="w-full h-full object-contain bg-stone-dark"
                >
                  <source src={currentDisplayUrl} type={currentVideoMime} />
                  Seu navegador não conseguiu reproduzir este vídeo.
                </video>
              ) : (
                <img
                  src={currentDisplayUrl}
                  alt={current?.nome || "Mídia do personagem"}
                  className="w-full h-full object-contain cursor-zoom-in"
                  onClick={() => openInNewWindow(currentDisplayUrl)}
                />
              )
            ) : (
              <span className="font-pixel text-[8px] text-muted-foreground">Carregando...</span>
            )}

            {videoLoading && currentIsVideo && (
              <span className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-sm bg-stone-dark/90 px-2 py-1 font-pixel text-[7px] text-primary tibia-border-inner">
                Preparando vídeo...
              </span>
            )}

            {currentDisplayUrl && (
              <button
                type="button"
                onClick={() => openInNewWindow(currentDisplayUrl)}
                aria-label="Abrir em tamanho original"
                title="Abrir em tamanho original"
                className="absolute top-1.5 right-1.5 z-10 w-8 h-8 flex items-center justify-center rounded-sm bg-stone-dark/80 tibia-border-inner text-primary hover:bg-stone-dark transition-colors"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            )}
          </div>



          {hasMany && (
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Próxima"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-sm bg-stone-dark/80 tibia-border-inner text-primary hover:bg-stone-dark transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Thumbnails row */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 mt-1">
          {thumbnailItems.map((m, i) => {
            const url = urls[m.path];
            const active = i === index;
            const isVideo = isVideoMedia(m);
            return (
              <button
                key={m.path}
                type="button"
                onClick={() => setIndex(i)}
                className={`shrink-0 w-[68px] flex flex-col items-center gap-0.5 text-center rounded-sm p-1 tibia-border-inner transition-colors ${active ? "bg-primary/20" : "bg-stone-dark/40 hover:bg-stone-dark/70"}`}
              >
                <span className="relative w-full h-12 rounded-sm overflow-hidden bg-stone-dark/60 flex items-center justify-center">
                  {url ? (
                    isVideo ? (
                      <>
                        <video src={url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                        <Film className="absolute w-3.5 h-3.5 text-primary drop-shadow" />
                      </>
                    ) : (
                      <img src={url} alt={m.nome} className="w-full h-full object-cover" />
                    )
                  ) : isVideo ? (
                    <Film className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ImageIcon className="w-4 h-4 text-muted-foreground" />
                  )}
                </span>
                <span className={`font-pixel text-[6px] leading-tight break-words line-clamp-2 ${active ? "text-primary" : "text-muted-foreground"}`}>
                  {m.nome}
                </span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaCarouselDialog;
